import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import PasswordStrengthBar from "@/components/ui/PasswordStrengthBar";
import MyRoutesPanel from "@/components/RoutePlanner/MyRoutesPanel";
import AuthModal from "@/components/RoutePlanner/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { register } from "@/services/authService";
import {
  getAccount,
  updateAccount,
  deleteAccount,
  resendVerificationEmail,
} from "@/services/accountService";
import {
  getAllRoutes,
  updateRoute,
  deleteRoute as deleteRouteApi,
} from "@/services/routeService";
import {
  Mail,
  Lock,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Shield,
  LogIn,
  LogOut,
  Trash2,
  Save,
} from "lucide-react";

const MyAccount = () => {
  const {
    user,
    isLoggedIn,
    isLoading: authLoading,
    login,
    logout,
    refreshUser,
  } = useAuth();

  // Account data
  const [account, setAccount] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);

  // Profile section
  const [profileEmail, setProfileEmail] = useState("");
  const [emailFormat, setEmailFormat] = useState("HTML");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  // Password section
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordCheck, setPasswordCheck] = useState(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const debounceRef = useRef(null);

  // Vacation section
  const [suspendStart, setSuspendStart] = useState("");
  const [suspendEnd, setSuspendEnd] = useState("");
  const [vacationSaving, setVacationSaving] = useState(false);
  const [vacationMessage, setVacationMessage] = useState(null);

  // Routes section
  const [savedRoutes, setSavedRoutes] = useState([]);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Error
  const [error, setError] = useState(null);

  // Auth modal state (for not-logged-in flow)
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authLoading2, setAuthLoading2] = useState(false);
  const [serverFieldErrors, setServerFieldErrors] = useState({});

  // Fetch account data
  const fetchAccount = useCallback(async () => {
    try {
      setLoadingAccount(true);
      const data = await getAccount();
      setAccount(data);
      setProfileEmail(data.email || "");
      setEmailFormat(data.format || "HTML");
      if (data.suspendAlertsPeriod) {
        setSuspendStart(data.suspendAlertsPeriod.start || "");
        setSuspendEnd(data.suspendAlertsPeriod.end || "");
      }
    } catch {
      // Will show login prompt
    } finally {
      setLoadingAccount(false);
    }
  }, []);

  const fetchRoutes = useCallback(async () => {
    try {
      const result = await getAllRoutes();
      setSavedRoutes(result?.data || []);
    } catch {
      setSavedRoutes([]);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchAccount();
      fetchRoutes();
    }
  }, [isLoggedIn, fetchAccount, fetchRoutes]);

  // Debounced password strength check
  useEffect(() => {
    if (!newPassword || newPassword.length < 4) {
      setPasswordCheck(null);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post("/lmiga/user/check-password.json", {
          password: newPassword,
        });
        setPasswordCheck(res.data?.data || null);
      } catch {
        setPasswordCheck(null);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [newPassword]);

  // Save profile
  const handleSaveProfile = async () => {
    try {
      setProfileSaving(true);
      setProfileMessage(null);
      await updateAccount({ email: profileEmail, format: emailFormat });
      setProfileMessage({ type: "success", text: "Profile updated." });
      refreshUser();
    } catch (err) {
      setProfileMessage({
        type: "error",
        text: err.message || "Failed to update profile.",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  // Save password
  const handleSavePassword = async () => {
    if (newPassword.length < 8) {
      setPasswordMessage({
        type: "error",
        text: "Password must be at least 8 characters.",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: "error", text: "Passwords do not match." });
      return;
    }
    try {
      setPasswordSaving(true);
      setPasswordMessage(null);
      await updateAccount({ password: newPassword });
      setPasswordMessage({ type: "success", text: "Password updated." });
      setNewPassword("");
      setConfirmPassword("");
      setPasswordCheck(null);
    } catch (err) {
      setPasswordMessage({
        type: "error",
        text: err.message || "Failed to update password.",
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  // Save vacation mode
  const handleSaveVacation = async () => {
    try {
      setVacationSaving(true);
      setVacationMessage(null);
      await updateAccount({
        suspendAlertsStart: suspendStart || null,
        suspendAlertsEnd: suspendEnd || null,
      });
      setVacationMessage({ type: "success", text: "Vacation mode updated." });
    } catch (err) {
      setVacationMessage({
        type: "error",
        text: err.message || "Failed to update vacation mode.",
      });
    } finally {
      setVacationSaving(false);
    }
  };

  const handleClearVacation = async () => {
    setSuspendStart("");
    setSuspendEnd("");
    try {
      setVacationSaving(true);
      await updateAccount({
        suspendAlertsStart: null,
        suspendAlertsEnd: null,
      });
      setVacationMessage({ type: "success", text: "Vacation mode cleared." });
    } catch (err) {
      setVacationMessage({
        type: "error",
        text: err.message || "Failed to clear vacation mode.",
      });
    } finally {
      setVacationSaving(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      await deleteAccount();
      await logout();
    } catch (err) {
      setError(err.message || "Failed to delete account.");
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Routes handlers (no map editing from My Account — just rename/delete/alerts)
  const handleRenameRoute = async (routeId, newName) => {
    try {
      await updateRoute(routeId, { displayName: newName });
      fetchRoutes();
    } catch {
      setError("Failed to rename route");
    }
  };

  const handleDeleteRoute = async (routeId) => {
    try {
      await deleteRouteApi(routeId);
      fetchRoutes();
    } catch {
      setError("Failed to delete route");
    }
  };

  const handleLogin = async (loginData) => {
    try {
      setAuthLoading2(true);
      setAuthError(null);
      await login(loginData.email, loginData.password);
      setAuthLoading2(false);
      setShowAuthModal(false);
    } catch (err) {
      setAuthLoading2(false);
      setAuthError(err.message || "Login failed.");
    }
  };

  const handleRegister = async (registerData) => {
    try {
      setAuthLoading2(true);
      setAuthError(null);
      setServerFieldErrors({});
      await register({
        email: registerData.email,
        password: registerData.password,
      });
      setAuthLoading2(false);
      setShowAuthModal(false);
    } catch (err) {
      setAuthLoading2(false);
      setAuthError(err.message || "Registration failed.");
      setServerFieldErrors(err.fieldErrors || {});
    }
  };

  // Not logged in
  if (!authLoading && !isLoggedIn) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>My Account</CardTitle>
            <CardDescription>
              Log in to manage your account, routes, and alert settings.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setShowAuthModal(true)} className="w-full">
              <LogIn className="mr-2 h-4 w-4" />
              Log In / Register
            </Button>
          </CardContent>
        </Card>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onLogin={handleLogin}
          onRegister={handleRegister}
          loading={authLoading2}
          error={authError}
          serverFieldErrors={serverFieldErrors}
        />
      </div>
    );
  }

  if (authLoading || loadingAccount) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">My Account</h1>
        <Button variant="outline" onClick={logout}>
          <LogOut className="mr-2 h-4 w-4" />
          Log Out
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Profile Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <div className="absolute left-3 top-3 text-gray-400">
                  <Mail className="h-4 w-4" />
                </div>
                <Input
                  type="email"
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
              {account?.emailVerified ? (
                <span className="flex items-center text-xs text-green-600 dark:text-green-400 gap-1 shrink-0">
                  <CheckCircle className="h-3 w-3" /> Verified
                </span>
              ) : (
                account && (
                  <span className="flex items-center text-xs text-yellow-600 dark:text-yellow-400 gap-1 shrink-0">
                    <AlertTriangle className="h-3 w-3" /> Not verified
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await resendVerificationEmail();
                          setProfileMessage({
                            type: "success",
                            text: "Verification email sent! Check your inbox.",
                          });
                        } catch {
                          setProfileMessage({
                            type: "error",
                            text: "Failed to resend verification email.",
                          });
                        }
                      }}
                      className="ml-1 underline font-medium"
                    >
                      Resend
                    </button>
                  </span>
                )
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Email Format</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="format"
                  value="HTML"
                  checked={emailFormat === "HTML"}
                  onChange={() => setEmailFormat("HTML")}
                  className="mr-2"
                />
                HTML
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="format"
                  value="TEXT"
                  checked={emailFormat === "TEXT"}
                  onChange={() => setEmailFormat("TEXT")}
                  className="mr-2"
                />
                Plain Text
              </label>
            </div>
          </div>
          {profileMessage && (
            <p
              className={`text-sm ${
                profileMessage.type === "success"
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {profileMessage.text}
            </p>
          )}
        </CardContent>
        <CardFooter>
          <Button onClick={handleSaveProfile} disabled={profileSaving}>
            <Save className="mr-2 h-4 w-4" />
            {profileSaving ? "Saving..." : "Save Profile"}
          </Button>
        </CardFooter>
      </Card>

      {/* Change Password Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Change Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">New Password</label>
            <div className="relative">
              <div className="absolute left-3 top-3 text-gray-400">
                <Lock className="h-4 w-4" />
              </div>
              <Input
                type="password"
                placeholder="Enter new password"
                className="pl-10"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            {passwordCheck && (
              <PasswordStrengthBar
                strength={passwordCheck.strength}
                valid={passwordCheck.valid}
                details={passwordCheck.details}
              />
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Confirm Password</label>
            <div className="relative">
              <div className="absolute left-3 top-3 text-gray-400">
                <Lock className="h-4 w-4" />
              </div>
              <Input
                type="password"
                placeholder="Confirm new password"
                className="pl-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            {newPassword &&
              confirmPassword &&
              newPassword !== confirmPassword && (
                <p className="text-red-500 text-xs">Passwords do not match</p>
              )}
          </div>
          {passwordMessage && (
            <p
              className={`text-sm ${
                passwordMessage.type === "success"
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {passwordMessage.text}
            </p>
          )}
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleSavePassword}
            disabled={passwordSaving || !newPassword}
          >
            <Save className="mr-2 h-4 w-4" />
            {passwordSaving ? "Saving..." : "Update Password"}
          </Button>
        </CardFooter>
      </Card>

      {/* Vacation Mode Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Vacation Mode</CardTitle>
          <CardDescription>
            Suspend email alerts during a date range
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <div className="relative">
                <div className="absolute left-3 top-3 text-gray-400">
                  <Calendar className="h-4 w-4" />
                </div>
                <Input
                  type="date"
                  className="pl-10"
                  value={suspendStart}
                  onChange={(e) => setSuspendStart(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <div className="relative">
                <div className="absolute left-3 top-3 text-gray-400">
                  <Calendar className="h-4 w-4" />
                </div>
                <Input
                  type="date"
                  className="pl-10"
                  value={suspendEnd}
                  onChange={(e) => setSuspendEnd(e.target.value)}
                />
              </div>
            </div>
          </div>
          {vacationMessage && (
            <p
              className={`text-sm ${
                vacationMessage.type === "success"
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {vacationMessage.text}
            </p>
          )}
        </CardContent>
        <CardFooter className="gap-2">
          <Button onClick={handleSaveVacation} disabled={vacationSaving}>
            <Save className="mr-2 h-4 w-4" />
            {vacationSaving ? "Saving..." : "Save"}
          </Button>
          {(suspendStart || suspendEnd) && (
            <Button
              variant="outline"
              onClick={handleClearVacation}
              disabled={vacationSaving}
            >
              Clear
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* My Routes Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">My Routes</CardTitle>
          <CardDescription>
            Manage your saved routes and alert settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MyRoutesPanel
            routes={savedRoutes}
            onEditRoute={() => {}} // Edit only works from Trip Map
            onRenameRoute={handleRenameRoute}
            onDeleteRoute={handleDeleteRoute}
            onConfigureAlerts={() => {}} // TODO: could open alert config modal
            editingRouteId={null}
            loading={false}
          />
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-red-600 dark:text-red-400">
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Permanently delete your account and all saved routes. This action
            cannot be undone.
          </p>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteConfirm(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Account
          </Button>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogDescription>
          Are you sure you want to permanently delete your account? All saved
          routes and alert settings will be removed. This cannot be undone.
        </DialogDescription>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteAccount}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deleting ? "Deleting..." : "Delete Account"}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default MyAccount;
